import * as A from 'astronomy-engine';
import { type SkyEvent, eventEnd, upcomingEvents } from '../astro/events';
import type { Starts } from '../astro/hijri';
import type { Shower } from '../astro/meteors';

// The sky calendar's sums with no page attached, so the events worker and the page share one copy: a place's
// events over a stretch of time, and a year of them a month at a time.

const DAY = 86400e3;

/** Where the sums are for, in plain numbers (all the events worker is sent of a place), with its time zone. */
export interface Spot { lat: number; lon: number; height?: number; tz?: string }

/** A place's events over `days` from `from`, soonest first. `starts`: Saudi Arabia's decided month starts. */
export function eventsOver(p: Spot, from: number, days: number, showers: Shower[], bortle: number, starts: Starts = {}): SkyEvent[] {
  return upcomingEvents(new A.Observer(p.lat, p.lon, p.height ?? 0), new Date(from), days, showers, { bortle, starts, tz: p.tz });
}

/** What "On now" and the reminders look at: from 30 days ago (for showers still active) to 45 days ahead. */
export const AROUND = { before: 30 * DAY, days: 75 };

// ---------- A year, a month at a time ----------
// Months overlap by a day and events are matched by id, so nothing falls between them.

const YEAR_DAYS = 366;
/** Still worth listing although it began before `from`: a shower still active, or an eclipse not yet over. */
const ongoing = (e: SkyEvent, from: number) => (e.active || e.phases || e.kind === 'day' ? eventEnd(e) >= from : false);

/**
 * The events of month `i` of the year from `from` that belong in the year. The first month also looks back a
 * month for showers still active and eclipses still under way.
 */
export function yearMonth(p: Spot, from: number, i: number, showers: Shower[], bortle: number, starts: Starts = {}): SkyEvent[] {
  const end = from + YEAR_DAYS * DAY;
  const start = i ? from + i * 30 * DAY - DAY : from - 30 * DAY;
  return eventsOver(p, start, i ? 32 : 62, showers, bortle, starts).filter((e) => (e.t >= from - 3600e3 || ongoing(e, from)) && e.t < end);
}

/** Whether the year is complete once its first `months` months are in (13 in all). */
export const yearDone = (months: number) => months * 30 >= YEAR_DAYS;

/** Adds a month's events to the year so far (a later month's copy of an event replaces the earlier one), and returns the year soonest first. */
export function addMonth(byId: Map<string, SkyEvent>, events: SkyEvent[]): SkyEvent[] {
  for (const e of events) byId.set(e.id, e);
  return [...byId.values()].sort((a, b) => a.t - b.t);
}
